import { FileText, Folder } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";
import {
	PrimaryButton,
	TextPrimaryButton,
} from "src/components/Default/LekUI/Button";
import type { FolderItemType } from "src/components/Folders/types";
import {
	Modal,
	ModalFooter,
	ModalHeader,
} from "src/components/Modals/Components/ModalsComponents";
import { FolderPickerList } from "src/components/Modals/FolderPickerList";
import { ShortLimitedAccess } from "src/components/NewComponents/LimitedAccess";
import { hasLimitedAccess } from "src/helpers/getPermissions";
import { useFolderPicker } from "src/hooks/useFolderPicker";
import { useAddFolderItems, useMoveFolderItems } from "src/hooks/useFolders";

interface MoveSelectedItem {
	id: string;
	itemId: string;
	itemType: FolderItemType;
}

interface MoveToFolderModalCustomLabels {
	title: string;
	actionLabel: string;
}

interface MoveToFolderModalProps {
	onClose: () => void;
	folderId?: string;
	selectedItems: MoveSelectedItem[];
	onSuccess?: () => void;
	isCopyMode?: boolean;
	custom?: MoveToFolderModalCustomLabels;
	multiSelect?: boolean;
}

export const MoveToFolderModal = ({
	onClose,
	folderId,
	selectedItems,
	onSuccess,
	isCopyMode = false,
	custom,
	multiSelect = true,
}: MoveToFolderModalProps) => {
	const { t } = useTranslation();
	const { mutateAsync: addItems, isPending: isAdding } = useAddFolderItems();
	const { mutateAsync: moveItems, isPending: isMoving } = useMoveFolderItems();

	const {
		selectedFolderIds,
		folders,
		isLoading,
		showCreateModal,
		isCreatingFolder,
		setShowCreateModal,
		toggleFolderSelection,
		handleCreateFolder,
	} = useFolderPicker({ multiSelect, excludeFolderId: folderId });

	const buildMoveBody = (targetFolderId: string) => {
		const body: {
			targetFolderId: number;
			examsIds: number[];
			courseIds: number[];
			notesIds: number[];
			decksIds: number[];
		} = {
			targetFolderId: Number(targetFolderId),
			examsIds: [],
			courseIds: [],
			notesIds: [],
			decksIds: [],
		};

		const typeMap: Record<FolderItemType, keyof typeof body> = {
			exam: "examsIds",
			course: "courseIds",
			note: "notesIds",
			deck: "decksIds",
		};

		for (const item of selectedItems) {
			const key = typeMap[item.itemType];
			if (key && key !== "targetFolderId") {
				(body[key] as number[]).push(Number(item.id));
			}
		}
		return body;
	};

	const handleMove = async () => {
		if (selectedFolderIds.length === 0 || selectedItems.length === 0) return;
		const addPayload = selectedItems.map(({ itemId, itemType }) => ({
			itemId,
			itemType,
		}));
		// Przenoszenie: pierwszy folder przez move (znika ze źródła), reszta jako kopie
		const [moveTargetId, ...addTargetIds] = isCopyMode
			? [undefined, ...selectedFolderIds]
			: selectedFolderIds;
		await Promise.all([
			moveTargetId &&
				moveItems({ folderId, body: buildMoveBody(moveTargetId) }),
			...addTargetIds.map((id) =>
				addItems({ folderId: id, items: addPayload }),
			),
		]);

		onSuccess?.();
		onClose();
	};

	const isSaving = isAdding || isMoving;
	const title =
		custom?.title ??
		(isCopyMode
			? t("folder.copyToFolderTitle", "Kopiuj do folderu")
			: t("folder.moveToFolderTitle", "Przenieś do folderu"));

	if (hasLimitedAccess()) {
		return (
			<Modal onClose={onClose} className="modal--fixed-footer">
				<ModalHeader Icon={Folder} title={title} hasCloseButton />
				<ShortLimitedAccess />
			</Modal>
		);
	}

	const selectedNames = folders
		.filter((f) => selectedFolderIds.includes(String(f.id)))
		.map((f) => f.name)
		.join(", ");

	return (
		<Modal onClose={onClose} className="modal--fixed-footer">
			{!showCreateModal && (
				<ModalHeader Icon={Folder} title={title} hasCloseButton />
			)}
			<FolderPickerList
				folders={folders}
				isLoading={isLoading}
				selectedFolderIds={selectedFolderIds}
				onToggle={toggleFolderSelection}
				showCreateModal={showCreateModal}
				isCreatingFolder={isCreatingFolder}
				onOpenCreateModal={() => setShowCreateModal(true)}
				onCloseCreateModal={() => setShowCreateModal(false)}
				onCreateFolder={handleCreateFolder}
				folderTestId="move-modal-folder-item"
				createButtonTestId="move-modal-create-folder-button"
				summary={
					<span className="chip chip--muted">
						<FileText className="icon-super-small icon-primary" />
						{isCopyMode
							? t("folder.copyingLabel", "Kopiujesz:")
							: t("folder.movingLabel", "Przenosisz:")}
						<strong>
							{t("folder.itemsCount", "{{count}} elementów", {
								count: selectedItems.length,
							})}
						</strong>
					</span>
				}
			/>
			{!showCreateModal && (
				<ModalFooter className="justify-between items-center gap-12 mt-16 pt-16 border-top-1">
					<span className="size-14-light text-grey">
						{selectedNames && (
							<Trans
								i18nKey="folder.targetFolders"
								defaults="Do folderu: <0>{{names}}</0>"
								values={{ names: selectedNames }}
								components={[<strong key="0" className="text-black" />]}
							/>
						)}
					</span>
					<div className="flex gap-12">
						<TextPrimaryButton onClick={onClose} disabled={isSaving}>
							{t("commands.cancel", "Anuluj")}
						</TextPrimaryButton>
						<PrimaryButton
							data-test-id="move-modal-save-button"
							onClick={handleMove}
							disabled={isSaving || selectedFolderIds.length === 0}
						>
							{custom?.actionLabel ??
								(isCopyMode
									? t("folder.copy", "Kopiuj")
									: t("folder.move", "Przenieś"))}
						</PrimaryButton>
					</div>
				</ModalFooter>
			)}
		</Modal>
	);
};
